import Alert from 'components/common/Alert';
import LoadingOverlay from 'components/common/LoadingOverlay';
import useProjectBudgetQuery from 'hooks/budget/useProjectBudgetQuery';
import { get, isNull } from 'lodash';
import LifestyleCalculatorForm from 'components/budget/form';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';
import { BUYING_HOUSE, NEW_APARTMENT, NEW_BUILDING, RENOVATION_HOUSE } from 'constants/constants';
import { BudgetCalculatorProjectType } from 'graphql/generated/graphql';
import { getInitialLifestyleCalculatorValues } from './LifestyleCalculator';
import { LifestyleOverview } from './LifestyleOverview';

const projectType2LifestyleProjectType: Partial<Record<string, BudgetCalculatorProjectType>> = {
  [NEW_BUILDING]: BudgetCalculatorProjectType.building,
  [RENOVATION_HOUSE]: BudgetCalculatorProjectType.renovation,
  [BUYING_HOUSE]: BudgetCalculatorProjectType.buying,
  [NEW_APARTMENT]: BudgetCalculatorProjectType.buying,
};

/** The lifestyle project type of a project type (`map[undefined]` was undefined as well). */
const toLifestyleProjectType = (type: string | undefined) => {
  if (type === undefined) return undefined;
  return projectType2LifestyleProjectType[type];
};

const LifestyleCalculatorWithProject = () => {
  // The route (`/secure/project/:id/lifestyle-overview`) always has the id.
  const { id: projectId } = useParams<{ id: string }>();
  const { myBudget, project, loading } = useProjectBudgetQuery(projectId);
  const { formatMessage: t } = useIntl();

  const formValues = get(project, 'form_values', {});
  const living_space = 'living_space' in formValues ? formValues.living_space : undefined;
  const apartment_area = 'apartment_area' in formValues ? formValues.apartment_area : undefined;
  const house_area = 'house_area' in formValues ? formValues.house_area : undefined;

  const squareMeters = useMemo(
    () => [living_space, apartment_area, house_area, 0].find(Number),
    [apartment_area, house_area, living_space],
  );
  const initialValues = useMemo(
    () => ({
      ...getInitialLifestyleCalculatorValues(myBudget),
      project_type: toLifestyleProjectType(project?.type),
      square_meters: squareMeters,
    }),
    [myBudget, squareMeters, project?.type],
  );
  const calculatorCompleted = useMemo(
    () => (!myBudget ? false : isNull(myBudget.step)),
    [myBudget],
  );
  const initialStep = useMemo(
    () => (!myBudget || isNull(myBudget.step) ? 1 : myBudget.step),
    [myBudget],
  );

  if (loading) {
    return <LoadingOverlay />;
  }

  if (!project) {
    return (
      <Alert color="danger" withContainer>
        {t({ id: 'errors.project_not_found' })}
      </Alert>
    );
  }

  if (!myBudget || !isNull(myBudget.step)) {
    return (
      <LifestyleCalculatorForm {...{ initialValues, myBudget, calculatorCompleted, initialStep }} />
    );
  }

  return <LifestyleOverview {...{ myBudget }} />;
};

export default LifestyleCalculatorWithProject;
